import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Leaf, Scissors, Sparkles, Lock } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const HERO = "https://static.prod-images.emergentagent.com/jobs/f2933716-8a33-4553-905d-fbbd725a7c18/images/1533cb96b6f2b5a6bdcf7452ed1d22d1debc5060c4aa5728976f804e1e95a9ed.jpeg";
const OVERSHIRT = "https://static.prod-images.emergentagent.com/jobs/f2933716-8a33-4553-905d-fbbd725a7c18/images/9177f01ffca581087a70603cd4af175c6cb7961307f591ab3fda182ae056e75c.jpeg";
const APRON = "https://static.prod-images.emergentagent.com/jobs/f2933716-8a33-4553-905d-fbbd725a7c18/images/e4131ae58f88a119865d8774912b56596d0c48d5deb82643ba4029d697b74da9.jpeg";
const TEE = "https://static.prod-images.emergentagent.com/jobs/f2933716-8a33-4553-905d-fbbd725a7c18/images/a44f6c96cf923f28c2062f67c6c3d88dcf5139261b921a22f67851c61f23e18c.jpeg";

const PRODUCTS = [
  { img: OVERSHIRT, name: "The Overshirt", material: "Heavyweight organic cotton", note: "A tailored workhorse layer — soft, structured, built to age well." },
  { img: APRON, name: "The Craftsman Apron", material: "Waxed canvas · full-grain leather", note: "Studio-grade protection with tool loops, cut for a full day at the chair." },
  { img: TEE, name: "The Emblem Tee", material: "Organic supima cotton", note: "The everyday essential, finished with a discreet gold Barbistry mark." },
];

const PILLARS = [
  { icon: Leaf, t: "Organic materials", d: "Responsibly sourced, natural fibers that feel as good as they look." },
  { icon: Scissors, t: "Made for the craft", d: "Designed with barbers, for the realities of a working chair." },
  { icon: Sparkles, t: "Limited drops", d: "Small, considered runs. No filler — only pieces worth owning." },
];

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.6, ease: [0.16, 1, 0.3, 1] } }),
};

export default function Reserve() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) { toast.error("Enter your email."); return; }
    try {
      setBusy(true);
      await api.post("/waitlist", { email, name, role: "client", source: "reserve" });
      setDone(true);
    } catch {
      toast.error("Something went wrong. Try again.");
    } finally { setBusy(false); }
  };

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/45" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-32 md:py-44">
          <motion.div initial="hidden" animate="show" variants={fade} custom={0}
            className="inline-flex items-center gap-2 border border-[#D4AF37]/40 px-3 py-1.5 mb-8">
            <Leaf size={14} className="text-[#D4AF37]" />
            <span className="text-xs font-mono uppercase tracking-widest text-[#D4AF37]">Coming Soon</span>
          </motion.div>
          <motion.h1 initial="hidden" animate="show" variants={fade} custom={1}
            className="font-display text-4xl sm:text-6xl lg:text-7xl font-bold leading-[1.05] max-w-3xl">
            Barbistry <span className="gold-text gold-glow-text">Reserve</span>.
          </motion.h1>
          <motion.p initial="hidden" animate="show" variants={fade} custom={2}
            className="mt-7 text-base md:text-lg text-neutral-300 max-w-xl leading-relaxed">
            High-end apparel and gear for the modern gentleman — cut from organic materials and built for the craft. Considered, limited, and made to last.
          </motion.p>
          <motion.a href="#reserve-waitlist" initial="hidden" animate="show" variants={fade} custom={3} className="inline-block mt-10">
            <Button className="rounded-none bg-[#D4AF37] text-black hover:bg-[#F0C84B] font-semibold h-12 px-8 text-base group" data-testid="reserve-hero-cta">
              Get first access <ArrowRight size={18} className="ml-2 transition-transform group-hover:translate-x-1" />
            </Button>
          </motion.a>
        </div>
      </section>

      {/* PILLARS */}
      <section className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <div className="grid gap-5 md:grid-cols-3">
          {PILLARS.map((p, i) => (
            <motion.div key={p.t} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade} custom={i}
              className="bg-[#111] border border-white/10 p-7 hover:border-[#D4AF37]/40 transition-colors">
              <p.icon className="text-[#D4AF37]" size={26} />
              <h3 className="mt-5 font-display text-lg font-semibold">{p.t}</h3>
              <p className="mt-2 text-sm text-neutral-400 leading-relaxed">{p.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* COLLECTION */}
      <section className="relative z-10 border-y border-white/10 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
          <div className="max-w-2xl mb-14">
            <p className="font-mono text-xs uppercase tracking-widest text-[#D4AF37] mb-4">First Drop · Preview</p>
            <h2 className="font-display text-3xl md:text-4xl font-bold">The founding collection.</h2>
            <p className="mt-3 text-neutral-400">A first look at what's coming. Pricing and release announced to the list first.</p>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {PRODUCTS.map((p, i) => (
              <motion.div key={p.name} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade} custom={i}
                className="group bg-[#111] border border-white/10 overflow-hidden hover:-translate-y-1 hover:border-[#D4AF37]/40 transition-transform duration-300"
                data-testid={`reserve-product-${i}`}>
                <div className="relative aspect-square overflow-hidden">
                  <img src={p.img} alt={p.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <span className="absolute top-3 right-3 flex items-center gap-1 bg-black/70 border border-[#D4AF37]/40 text-[#D4AF37] text-xs font-mono px-2 py-1"><Lock size={11} /> Coming Soon</span>
                </div>
                <div className="p-6">
                  <h3 className="font-display text-xl font-semibold">{p.name}</h3>
                  <p className="mt-1 text-xs font-mono uppercase tracking-wide text-[#D4AF37]">{p.material}</p>
                  <p className="mt-3 text-sm text-neutral-400 leading-relaxed">{p.note}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WAITLIST */}
      <section id="reserve-waitlist" className="relative z-10 max-w-5xl mx-auto px-5 sm:px-8 py-24">
        <div className="border border-[#D4AF37]/30 bg-gradient-to-b from-[#111] to-[#0a0a0a] p-8 md:p-12 gold-glow">
          <p className="font-mono text-xs uppercase tracking-widest text-[#D4AF37] mb-4">The Reserve List</p>
          <h2 className="font-display text-3xl md:text-5xl font-bold max-w-2xl">Be first to wear it.</h2>
          <p className="mt-4 text-neutral-300 max-w-xl">Join the Reserve list for early access, founding pricing, and first pick when the drop lands. No payment now — just your spot in line.</p>

          {done ? (
            <div className="mt-8 flex items-center gap-3 text-[#D4AF37]" data-testid="reserve-success">
              <CheckCircle2 size={26} />
              <div>
                <p className="font-display text-xl font-semibold">You're on the Reserve list.</p>
                <p className="text-sm text-neutral-400">We'll email you the moment the first drop is ready.</p>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-8 space-y-4" data-testid="reserve-form">
              <div className="grid sm:grid-cols-2 gap-3">
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" data-testid="reserve-name" className="bg-[#0a0a0a] border-white/15 rounded-none focus-visible:ring-[#D4AF37]" />
                <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" data-testid="reserve-email" className="bg-[#0a0a0a] border-white/15 rounded-none focus-visible:ring-[#D4AF37]" />
              </div>
              <Button type="submit" disabled={busy} data-testid="reserve-submit" className="rounded-none bg-[#D4AF37] text-black hover:bg-[#F0C84B] font-semibold h-12 px-8 text-base group">
                {busy ? "Joining…" : <>Join the Reserve List <ArrowRight size={18} className="ml-2 transition-transform group-hover:translate-x-1" /></>}
              </Button>
              <p className="text-xs text-neutral-500">No spam. Only Reserve drop news and your early-access invite.</p>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
